// ---------------------------------------------------------------------------
// Projects (execution after a deal is won): the project itself, its logs (submittals, RFIs, risks,
// materials, site work, testing, snags, timesheets, costs, billing, assets, service tickets) and
// Project Intelligence (health score and warnings).
// The logs are described here once; the API validates and stores them and the web app renders them
// from the same definitions.
// ---------------------------------------------------------------------------
import { z } from 'zod';
import { QUOTE_SYSTEMS } from './quotedBrands';

type Opt = { value: string; label: string };
const o = (...pairs: [string, string][]): Opt[] => pairs.map(([value, label]) => ({ value, label }));

export const PROJECT_STATUSES = o(
  ['handover', 'Handover from sales'], ['engineering', 'Engineering & submittals'], ['procurement', 'Procurement'],
  ['installation', 'Installation'], ['testing', 'Testing & commissioning'], ['customer_handover', 'Handover to customer'],
  ['warranty', 'Warranty'], ['closed', 'Closed'], ['on_hold', 'On hold'],
);

/** Sales → execution: what must be in hand before the project starts. */
export const START_CHECKLIST = o(
  ['po_received', 'Customer PO received'], ['contract_signed', 'Contract signed'], ['final_boq', 'Final BOQ agreed'],
  ['approved_quote', 'Approved quotation revision'], ['payment_terms', 'Payment terms agreed'], ['site_contacts', 'Site contacts known'],
  ['logistics_handover', 'Handover to logistics accepted'],
);
/** Sales-to-execution items that are commercial (hidden from a logistics-only viewer). */
export const COMMERCIAL_CHECKLIST_KEYS = ['po_received', 'contract_signed', 'final_boq', 'approved_quote', 'payment_terms'];
/** Project sections a logistics-only viewer may read (operational only: no BOQ / customer PO, money or commercial terms). */
export const LOGISTICS_SECTIONS = ['materials', 'phases', 'schedule', 'lookahead', 'areas', 'points'];
/** Handover to the customer. */
/** A project type's key: the three built-in ones, or one added in Setup → Project types. */
export type ProjectType = string;
export const projectTypeKey = z.string().regex(/^[a-z0-9_]{1,40}$/, 'Choose a project type.');
/** Configuration-only projects: the steps before the handover file. */
export const CONFIG_CHECKLIST = o(
  ['cfg_materials', 'Materials delivered to the customer'], ['cfg_installed', 'Customer finished the installation'],
  ['cfg_configured', 'Systems configured'], ['cfg_tested', 'Tested & commissioned'], ['cfg_acceptance', 'Customer acceptance signed'],
);
/** Tabs a configuration-only project doesn't need (the customer installs). */
export const CONFIG_HIDDEN_TABS = ['plan', 'engineering', 'site', 'quality'];
/** Supply-only projects: Danat delivers the materials on the PO, nothing else (no installation, configuration or testing). */
export const SUPPLY_CHECKLIST = o(
  ['sup_ordered', 'Materials ordered from suppliers'], ['sup_received', 'Materials received in our warehouse'],
  ['sup_delivered', 'Materials delivered to the customer'], ['sup_dn_signed', 'Delivery note signed by the customer'], ['sup_invoiced', 'Invoiced by accounts'],
);
/**
 * Tabs a supply-only project doesn't need. Time & cost (site hours) goes; Purchase requests shows inside Materials
 * (one list: what to buy, requested, ordered, received).
 */
export const SUPPLY_HIDDEN_TABS = ['plan', 'engineering', 'site', 'quality', 'testing', 'money', 'orders'];
/** A supply-only kind of project (built-in or the organization's own type): no site work, materials only. */
export const isSupplyLike = (hiddenTabs: string[]) => hiddenTabs.includes('site') && hiddenTabs.includes('money');

/** Project tabs a project type can hide (Overview, Documents and Handover always show). */
export const PROJECT_TYPE_TABS = o(
  ['boq', 'BOQ'], ['plan', 'Planning'], ['engineering', 'Submittals & RFIs'], ['orders', 'Purchase requests'], ['materials', 'Materials'],
  ['site', 'Site'], ['testing', 'Testing & snags'], ['quality', 'Quality & safety'], ['commercial', 'Commercial'], ['money', 'Time & cost'], ['service', 'Service'],
);

/**
 * A project type (Setup → Project types): its name, the tabs it hides, its own checklist and whether the handover file
 * (as-built, O&M…) applies. With a checklist, the project's progress follows it (plus the handover file when it applies);
 * without one, progress follows the systems & points, work areas or status, like a full installation.
 */
export interface ProjectTypeDef {
  key: string;
  label: string;
  hiddenTabs: string[];
  checklist: { value: string; label: string }[];
  withHandover: boolean;
  isActive: boolean;
  /** Billing milestones a new project of this type starts with (% of the contract); the project manager can change them. */
  milestones: { name: string; percent: number }[];
}

const STANDARD_MILESTONES = [{ name: 'Advance payment', percent: 10 }, { name: 'Material delivery', percent: 70 }, { name: 'Handover', percent: 20 }];

/** The built-in types (also what a new organization starts with). */
export const DEFAULT_PROJECT_TYPES: ProjectTypeDef[] = [
  { key: 'full', label: 'Full installation (Danat installs)', hiddenTabs: [], checklist: [], withHandover: true, isActive: true, milestones: STANDARD_MILESTONES },
  { key: 'configuration', label: 'Configuration only (customer installs)', hiddenTabs: CONFIG_HIDDEN_TABS, checklist: CONFIG_CHECKLIST, withHandover: true, isActive: true, milestones: STANDARD_MILESTONES },
  { key: 'supply', label: 'Supply only (we deliver the materials)', hiddenTabs: SUPPLY_HIDDEN_TABS, checklist: SUPPLY_CHECKLIST, withHandover: false, isActive: true, milestones: [{ name: 'Payment with PO', percent: 100 }] },
];

export const projectTypesSaveSchema = z.object({
  types: z.array(z.object({
    key: z.string().regex(/^[a-z0-9_]{1,40}$/).optional(),
    label: z.string().trim().min(1, 'Give each type a name.').max(80),
    hiddenTabs: z.array(z.string().max(40)).max(20),
    checklist: z.array(z.object({ value: z.string().regex(/^[a-z0-9_]{1,40}$/).optional(), label: z.string().trim().min(1).max(120) })).max(30),
    withHandover: z.boolean(),
    isActive: z.boolean(),
    milestones: z.array(z.object({ name: z.string().trim().min(1).max(120), percent: z.number().min(0).max(100) })).max(12).default([])
      .refine((m) => m.reduce((a, x) => a + x.percent, 0) <= 100.001, 'The billing milestones add up to more than 100%.'),
  })).min(1).max(30),
}).strict();

/** A short key from a name ("Supply & install" → "supply_install"). */
export function slugKey(label: string, taken: Set<string>, prefix = ''): string {
  const base = (prefix + label.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')).slice(0, 34) || `${prefix}item`;
  let k = base; let n = 2;
  while (taken.has(k)) k = `${base}_${n++}`;
  taken.add(k);
  return k;
}

export const HANDOVER_CHECKLIST = o(
  ['as_built', 'As-built drawings'], ['om_manuals', 'O&M manuals'], ['warranty_certs', 'Warranty certificates'],
  ['training', 'Training done'], ['test_reports', 'Testing & commissioning reports'], ['spares', 'Spare parts handed over'],
);

export const PROJECT_SYSTEMS: Opt[] = [...QUOTE_SYSTEMS.filter((s) => s.value !== 'other').map((s) => ({ value: s.value, label: s.label })), { value: 'fire_alarm', label: 'Fire Alarm' }, { value: 'other', label: 'Other' }, { value: 'general', label: 'General' }];
const SYSTEMS = PROJECT_SYSTEMS;

/** Cost types (costs and budget). */
export const COST_KINDS = o(['material', 'Material'], ['labour', 'Labour'], ['subcontract', 'Subcontract'], ['transport', 'Transport'], ['other', 'Other']);

/** Variation orders: approved internally by an administrator, then by the customer. */
export const VO_STATUSES = o(
  ['draft', 'Draft'], ['pending', 'Waiting for internal approval'], ['approved_internal', 'Approved internally'], ['rejected_internal', 'Rejected internally'],
  ['submitted_customer', 'Submitted to customer'], ['approved_customer', 'Approved by customer'], ['rejected_customer', 'Rejected by customer'],
);

/** Purchase orders need an administrator's approval before they're sent to the supplier. */
export const PO_STATUSES = o(
  ['draft', 'Draft'], ['pending', 'Waiting for administrator'], ['approved', 'Approved'], ['rejected', 'Rejected'], ['ordered', 'Ordered from supplier'], ['delivered', 'Delivered to site'],
);

export type ColType = 'text' | 'textarea' | 'number' | 'money' | 'percent' | 'date' | 'select' | 'user';
export interface SectionCol {
  name: string;
  column: string;
  label: string;
  type: ColType;
  options?: Opt[];
  /** A select that also keeps values outside its options (e.g. submittal types pre-sales added themselves). */
  allowCustom?: boolean;
  required?: boolean;
  /** Shown in the table (the rest only in the form). */
  list?: boolean;
  /** Money columns hidden from project engineers. */
  finance?: boolean;
  /** Shown but set by the CRM (e.g. who approved). */
  readOnly?: boolean;
  /** Filled by an administrator only (read-only for project managers and engineers). */
  adminOnly?: boolean;
  /** Seen by administrators only (supplier, price): hidden from everyone else, also in reports. */
  adminView?: boolean;
  default?: string | number;
}
export interface Section {
  key: string;
  table: string;
  label: string;
  singular: string;
  /** Reference numbers like SUB-001. */
  refPrefix?: string;
  /** Managers only (engineers don't see it). */
  finance?: boolean;
  /** Engineers may only add and see their own rows (timesheets). */
  ownRowsOnly?: boolean;
  /** Everyone on the project sees it; only project managers change it (the BOQ). */
  manageOnly?: boolean;
  order: string;
  cols: SectionCol[];
}

const c = (name: string, column: string, label: string, type: ColType, more: Partial<SectionCol> = {}): SectionCol => ({ name, column, label, type, ...more });

/** Invoice requests: what the project manager asks accounts to invoice in Odoo. */
export const INVOICE_REQUEST_KINDS = o(['milestone', 'Billing milestone'], ['progress', 'Progress claim (% of work)'], ['variation', 'Variation order'], ['retention', 'Retention release']);
export const INVOICE_REQUEST_STATUSES = o(['requested', 'Waiting for accounts'], ['pi_sent', 'PI sent, waiting for payment'], ['pi_paid', 'PI paid, tax invoice raised'],
  ['invoiced', 'Invoiced in Odoo'], ['rejected', 'Returned by accounts']);
/** PI: proforma invoice made outside Odoo, paid before the tax invoice; tax invoice: made in Odoo. */
export const INVOICE_DOC_TYPES = o(['tax', 'Tax invoice'], ['pi', 'Proforma invoice (PI)']);

export const PROJECT_SECTIONS: Section[] = [
  {
    // The project manager asks accounts to invoice; accounts make the invoice in Odoo and record its number here.
    key: 'invoicereq', table: 'project_invoice_requests', label: 'Invoice requests', singular: 'Invoice request', refPrefix: 'IR', finance: true, order: 'created_at desc',
    cols: [
      c('kind', 'kind', 'What to invoice', 'select', { options: INVOICE_REQUEST_KINDS, required: true, list: true, default: 'milestone' }),
      c('docType', 'doc_type', 'Document', 'select', { options: INVOICE_DOC_TYPES, required: true, list: true, default: 'tax' }),
      c('description', 'description', 'Details (milestone, period, VO no.…)', 'textarea', { required: true, list: true }),
      c('percent', 'percent', '% (of contract or work done)', 'percent', { list: true }),
      c('amount', 'amount', 'Amount before VAT (SAR)', 'money', { list: true }),
      c('status', 'status', 'Status', 'select', { options: INVOICE_REQUEST_STATUSES, readOnly: true, list: true }),
      c('piNo', 'pi_no', 'PI no.', 'text', { readOnly: true, list: true }),
      c('piPaidDate', 'pi_paid_date', 'PI paid on', 'date', { readOnly: true }),
      c('odooInvoiceNo', 'odoo_invoice_no', 'Odoo invoice no.', 'text', { readOnly: true, list: true }),
      c('invoicedDate', 'invoiced_date', 'Invoiced on', 'date', { readOnly: true, list: true }),
      c('accountsNote', 'accounts_note', 'Accounts note', 'text', { readOnly: true }),
    ],
  },
  {
    // The customer's PO, line by line: read from the PO PDF (or pasted from Excel), checked by the project manager.
    key: 'boq', table: 'project_boq', label: 'BOQ (customer PO)', singular: 'BOQ line', manageOnly: true, order: 'sort_order, created_at',
    cols: [
      c('lineNo', 'line_no', 'Item no.', 'text', { list: true }),
      c('description', 'description', 'Description', 'textarea', { required: true, list: true }),
      c('system', 'system', 'System', 'select', { options: SYSTEMS }),
      c('brand', 'brand', 'Brand', 'text', { list: true }),
      c('model', 'model', 'Part no.', 'text', { list: true }),
      c('unit','unit', 'Unit', 'text', { list: true, default: 'pcs' }),
      c('qty', 'qty', 'Qty', 'number', { list: true }),
      c('unitPrice', 'unit_price', 'Unit price', 'money', { list: true }),
      c('total', 'total', 'Total', 'money', { list: true }),
      c('source', 'source', 'From', 'text', { readOnly: true }),
    ],
  },
  {
    key: 'submittals', table: 'project_submittals', label: 'Submittals', singular: 'Submittal', refPrefix: 'SUB', order: 'created_at',
    cols: [
      c('title', 'title', 'Title', 'text', { required: true, list: true }),
      c('system', 'system', 'System', 'select', { options: SYSTEMS, list: true }),
      c('type', 'type', 'Type', 'select', { options: o(['material', 'Material submittal'], ['design_drawing', 'Design drawing (rack elevation, FOV, riser, layout)'], ['shop_drawing', 'Shop drawing'], ['datasheet', 'Technical datasheet'], ['calculation', 'Calculation / report'], ['compliance', 'Compliance statement'], ['method_statement', 'Method statement'], ['other', 'Other']), allowCustom: true, list: true, default: 'material' }),
      c('revision', 'revision', 'Revision', 'text', { list: true, default: 'R0' }),
      c('sentDate', 'sent_date', 'Sent on', 'date', { list: true }),
      c('dueDate', 'due_date', 'Reply due', 'date', { list: true }),
      c('status', 'status', 'Status / code', 'select', { options: o(['not_sent', 'Not sent'], ['under_review', 'Under review'], ['A', 'A – Approved'], ['B', 'B – Approved with comments'], ['C', 'C – Revise & resubmit'], ['rejected', 'Rejected']), list: true, default: 'not_sent' }),
      c('replyDate', 'reply_date', 'Reply on', 'date'),
      c('ownerId', 'owner_id', 'Owner', 'user', { list: true }),
      c('comments', 'comments', 'Consultant comments', 'textarea'),
    ],
  },
  {
    key: 'rfis', table: 'project_rfis', label: 'RFIs', singular: 'RFI', refPrefix: 'RFI', order: 'created_at',
    cols: [
      c('title', 'title', 'Question', 'text', { required: true, list: true }),
      c('toParty', 'to_party', 'Asked to', 'select', { options: o(['consultant', 'Consultant'], ['contractor', 'Main contractor'], ['client', 'Client'], ['supplier', 'Supplier']), list: true, default: 'consultant' }),
      c('raisedDate', 'raised_date', 'Raised on', 'date', { list: true }),
      c('dueDate', 'due_date', 'Answer due', 'date', { list: true }),
      c('status', 'status', 'Status', 'select', { options: o(['open', 'Open'], ['answered', 'Answered'], ['closed', 'Closed']), list: true, default: 'open' }),
      c('ownerId', 'owner_id', 'Owner', 'user', { list: true }),
      c('answer', 'answer', 'Answer', 'textarea'),
      c('impact', 'impact', 'Impact if late', 'text'),
    ],
  },
  {
    key: 'risks', table: 'project_risks', label: 'Risks & issues', singular: 'Risk / issue', refPrefix: 'RSK', order: 'created_at',
    cols: [
      c('title', 'title', 'Risk / issue', 'text', { required: true, list: true }),
      c('kind', 'kind', 'Kind', 'select', { options: o(['risk', 'Risk'], ['issue', 'Issue']), list: true, default: 'risk' }),
      c('severity', 'severity', 'Severity', 'select', { options: o(['low', 'Low'], ['medium', 'Medium'], ['high', 'High']), list: true, default: 'medium' }),
      c('ownerId', 'owner_id', 'Owner', 'user', { list: true }),
      c('dueDate', 'due_date', 'Due', 'date', { list: true }),
      c('status', 'status', 'Status', 'select', { options: o(['open', 'Open'], ['closed', 'Closed']), list: true, default: 'open' }),
      c('mitigation', 'mitigation', 'Action / mitigation', 'textarea'),
    ],
  },
  {
    key: 'materials', table: 'project_materials', label: 'Materials', singular: 'Material line', order: 'sort_order, created_at',
    cols: [
      // A project can have several customer POs: each line says which one it belongs to.
      c('customerPo', 'customer_po', 'Customer PO', 'text', { list: true }),
      c('system', 'system', 'System', 'select', { options: SYSTEMS, list: true }),
      c('description', 'description', 'Description', 'text', { required: true, list: true }),
      c('brand', 'brand', 'Brand', 'text', { list: true }),
      c('model', 'model', 'Part no.', 'text', { list: true }),
      c('qty', 'qty', 'Qty', 'number', { list: true }),
      c('unit', 'unit', 'Unit', 'text', { default: 'pcs', list: true }),
      c('unitCost', 'unit_cost', 'Unit cost', 'money', { finance: true, list: true }),
      c('quotedPrice', 'quoted_price', 'Quoted unit price', 'money', { finance: true }),
      c('supplier', 'supplier', 'Supplier', 'text', { list: true }),
      c('poNumber', 'po_number', 'Supplier PO', 'text'),
      c('orderDate', 'order_date', 'Ordered on', 'date'),
      c('expectedDate', 'expected_date', 'Expected delivery', 'date', { list: true }),
      c('requiredOnSite', 'required_on_site', 'Required on site', 'date', { list: true }),
      c('criticality', 'criticality', 'Criticality', 'select', { options: o(['critical', 'Critical (delays site work)'], ['normal', 'Normal']), list: true }),
      c('status', 'status', 'Status', 'select', { options: o(['to_order', 'To order'], ['ordered', 'Ordered'], ['shipped', 'Shipped'], ['delivered', 'Delivered to site'], ['installed', 'Installed']), list: true, default: 'to_order' }),
      c('qtyDelivered', 'qty_delivered', 'Qty delivered to customer', 'number'),
      c('qtyInWarehouse', 'qty_in_warehouse', 'Qty in our warehouse', 'number'),
      c('qtyInstalled', 'qty_installed', 'Qty installed', 'number'),
      c('notes', 'notes', 'Notes', 'textarea'),
    ],
  },
  {
    // Project managers request; the administrator approves and deals with the supplier.
    key: 'orders', table: 'project_purchase_orders', label: 'Purchase requests', singular: 'Purchase request', refPrefix: 'PR', finance: true, order: 'created_at desc',
    cols: [
      c('description', 'description', 'Items & quantities needed', 'textarea', { required: true, list: true }),
      c('system', 'system', 'System', 'select', { options: SYSTEMS, list: true }),
      c('neededBy', 'needed_by', 'Needed on site by', 'date', { list: true }),
      c('status', 'status', 'Status', 'select', { options: PO_STATUSES, list: true, default: 'pending', adminOnly: true }),
      c('supplier', 'supplier', 'Supplier', 'text', { list: true, adminOnly: true, adminView: true }),
      c('amount', 'amount', 'Amount (SAR)', 'money', { list: true, adminOnly: true, adminView: true }),
      c('poNumber', 'po_number', 'Supplier PO no.', 'text', { list: true, adminOnly: true, adminView: true }),
      c('decidedBy', 'decided_by', 'Decided by', 'user', { readOnly: true, list: true }),
      c('decidedAt', 'decided_at', 'Decided on', 'date', { readOnly: true }),
      c('decisionComment', 'decision_comment', 'Administrator comment', 'text', { readOnly: true }),
      c('notes', 'notes', 'Notes', 'textarea'),
    ],
  },
  {
    key: 'areas', table: 'project_areas', label: 'Site work', singular: 'Work area', order: 'system, area',
    cols: [
      c('system', 'system', 'System', 'select', { options: SYSTEMS, required: true, list: true }),
      c('area', 'area', 'Floor / zone', 'text', { required: true, list: true }),
      c('percent', 'percent', '% complete', 'percent', { list: true, default: 0 }),
      c('plannedEnd', 'planned_end', 'Planned finish', 'date', { list: true }),
      c('ownerId', 'owner_id', 'Engineer', 'user', { list: true }),
      c('notes', 'notes', 'Notes', 'textarea'),
    ],
  },
  {
    key: 'points', table: 'project_points', label: 'Systems & points', singular: 'Points line', order: 'system, area, created_at',
    cols: [
      c('system', 'system', 'System', 'select', { options: SYSTEMS, required: true, list: true }),
      c('item', 'item', 'Device / point', 'text', { required: true, list: true }),
      c('area', 'area', 'Floor / zone', 'text', { list: true }),
      c('total', 'total', 'Total points', 'number', { required: true, list: true }),
      c('cabled', 'cabled', 'Cabled', 'number', { list: true, default: 0 }),
      c('installed', 'installed', 'Installed', 'number', { list: true, default: 0 }),
      c('configured', 'configured', 'Terminated & configured', 'number', { list: true, default: 0 }),
      c('tested', 'tested', 'Tested & done', 'number', { list: true, default: 0 }),
      c('ownerId', 'owner_id', 'Engineer', 'user', { list: true }),
      c('notes', 'notes', 'Notes', 'textarea'),
    ],
  },
  {
    key: 'reports', table: 'project_site_reports', label: 'Daily site reports', singular: 'Site report', order: 'report_date desc, created_at desc',
    cols: [
      c('reportDate', 'report_date', 'Date', 'date', { required: true, list: true }),
      c('manpower', 'manpower', 'Manpower', 'number', { list: true }),
      c('workDone', 'work_done', 'Work done', 'textarea', { required: true, list: true }),
      c('issues', 'issues', 'Problems / delays', 'textarea', { list: true }),
      c('photosCount', 'photos_count', 'Photos', 'number', { readOnly: true, list: true }),
      c('photosUrl', 'photos_url', 'Photos folder', 'text', { readOnly: true }),
    ],
  },
  {
    key: 'tests', table: 'project_tests', label: 'Testing & commissioning', singular: 'Test item', order: 'system, sort_order, created_at',
    cols: [
      c('system', 'system', 'System', 'select', { options: SYSTEMS, required: true, list: true }),
      c('item', 'item', 'Check', 'text', { required: true, list: true }),
      c('location', 'location', 'Location', 'text', { list: true }),
      c('status', 'status', 'Result', 'select', { options: o(['pending', 'Pending'], ['passed', 'Passed'], ['failed', 'Failed'], ['n_a', 'Not applicable']), list: true, default: 'pending' }),
      c('checkedDate', 'checked_date', 'Checked on', 'date', { list: true }),
      c('checkedBy', 'checked_by', 'Checked by', 'user', { list: true }),
      c('notes', 'notes', 'Notes', 'textarea'),
    ],
  },
  {
    key: 'snags', table: 'project_snags', label: 'Snag list', singular: 'Snag', refPrefix: 'SNG', order: 'created_at',
    cols: [
      c('title', 'title', 'Snag', 'text', { required: true, list: true }),
      c('location', 'location', 'Location', 'text', { list: true }),
      c('system', 'system', 'System', 'select', { options: SYSTEMS, list: true }),
      c('raisedBy', 'raised_by', 'Raised by', 'select', { options: o(['consultant', 'Consultant'], ['client', 'Client'], ['contractor', 'Main contractor'], ['internal', 'Internal']), list: true, default: 'consultant' }),
      c('ownerId', 'owner_id', 'Owner', 'user', { list: true }),
      c('dueDate', 'due_date', 'Due', 'date', { list: true }),
      c('status', 'status', 'Status', 'select', { options: o(['open', 'Open'], ['fixed', 'Fixed'], ['closed', 'Closed by consultant']), list: true, default: 'open' }),
      c('notes', 'notes', 'Notes', 'textarea'),
    ],
  },
  {
    key: 'timesheets', table: 'project_timesheets', label: 'Timesheets', singular: 'Time entry', ownRowsOnly: true, order: 'work_date desc, created_at desc',
    cols: [
      c('workDate', 'work_date', 'Date', 'date', { required: true, list: true }),
      c('userId', 'user_id', 'Engineer', 'user', { required: true, list: true }),
      c('hours', 'hours', 'Hours', 'number', { required: true, list: true }),
      c('description', 'description', 'Work', 'text', { list: true }),
    ],
  },
  {
    key: 'costs', table: 'project_costs', label: 'Costs', singular: 'Cost', finance: true, order: 'cost_date desc, created_at desc',
    cols: [
      c('costDate', 'cost_date', 'Date', 'date', { list: true }),
      c('kind', 'kind', 'Kind', 'select', { options: COST_KINDS, list: true, default: 'material' }),
      c('system', 'system', 'System', 'select', { options: SYSTEMS, list: true }),
      c('description', 'description', 'Description', 'text', { required: true, list: true }),
      c('supplier', 'supplier', 'Supplier', 'text', { list: true }),
      c('amount', 'amount', 'Amount (SAR)', 'money', { required: true, list: true }),
      c('reference', 'reference', 'Reference / invoice', 'text'),
    ],
  },
  {
    key: 'milestones', table: 'project_milestones', label: 'Billing milestones', singular: 'Milestone', finance: true, order: 'due_date nulls last, created_at',
    cols: [
      c('name', 'name', 'Milestone', 'text', { required: true, list: true }),
      c('percent', 'percent', '% of contract', 'percent', { list: true }),
      c('amount', 'amount', 'Amount (SAR)', 'money', { list: true }),
      c('dueDate', 'due_date', 'Due', 'date', { list: true }),
      c('creditDays', 'credit_days', 'Credit days', 'number'),
      c('docType', 'doc_type', 'Billed with', 'select', { options: o(['pi', 'PI first'], ['tax', 'Tax invoice']), list: true }),
      c('status', 'status', 'Status', 'select', { options: o(['planned', 'Planned'], ['invoiced', 'Invoiced'], ['paid', 'Paid']), list: true, default: 'planned' }),
      c('invoiceNo', 'invoice_no', 'Invoice no.', 'text', { list: true }),
      c('invoicedDate', 'invoiced_date', 'Invoiced on', 'date'),
      c('paidDate', 'paid_date', 'Paid on', 'date'),
    ],
  },
  {
    key: 'assets', table: 'project_assets', label: 'Installed assets', singular: 'Asset', order: 'system, device',
    cols: [
      c('system', 'system', 'System', 'select', { options: SYSTEMS, list: true }),
      c('device', 'device', 'Device', 'text', { required: true, list: true }),
      c('brand', 'brand', 'Brand', 'text', { list: true }),
      c('model', 'model', 'Model', 'text', { list: true }),
      c('serial', 'serial', 'Serial no.', 'text', { list: true }),
      c('location', 'location', 'Location', 'text', { list: true }),
      c('installedDate', 'installed_date', 'Installed on', 'date'),
      c('warrantyEnd', 'warranty_end', 'Warranty until', 'date', { list: true }),
    ],
  },
  {
    key: 'tickets', table: 'project_tickets', label: 'Service tickets', singular: 'Ticket', refPrefix: 'TKT', order: 'created_at desc',
    cols: [
      c('title', 'title', 'Problem', 'text', { required: true, list: true }),
      c('priority', 'priority', 'Priority', 'select', { options: o(['low', 'Low'], ['normal', 'Normal'], ['high', 'High'], ['urgent', 'Urgent']), list: true, default: 'normal' }),
      c('status', 'status', 'Status', 'select', { options: o(['open', 'Open'], ['assigned', 'Assigned'], ['in_progress', 'In progress'], ['resolved', 'Resolved'], ['closed', 'Closed']), list: true, default: 'open' }),
      c('assignedTo', 'assigned_to', 'Engineer', 'user', { list: true }),
      c('reportedDate', 'reported_date', 'Reported on', 'date', { list: true }),
      c('responseDue', 'response_due', 'Response due', 'date', { list: true }),
      c('resolvedDate', 'resolved_date', 'Resolved on', 'date'),
      c('description', 'description', 'Details', 'textarea'),
      c('resolution', 'resolution', 'Resolution', 'textarea'),
    ],
  },
  // --- Planning & schedule ------------------------------------------------------------------------
  {
    key: 'phases', table: 'project_phases', label: 'Timeline', singular: 'Phase / milestone', order: 'start_date nulls last, sort_order, created_at',
    cols: [
      c('name', 'name', 'Phase / milestone', 'text', { required: true, list: true }),
      c('kind', 'kind', 'Kind', 'select', { options: o(['phase', 'Phase'], ['milestone', 'Milestone']), list: true, default: 'phase' }),
      c('startDate', 'start_date', 'Start', 'date', { list: true }),
      c('endDate', 'end_date', 'Finish', 'date', { list: true }),
      c('percent', 'percent', '% done', 'percent', { list: true, default: 0 }),
      c('status', 'status', 'Status', 'select', { options: o(['not_started', 'Not started'], ['in_progress', 'In progress'], ['done', 'Done'], ['delayed', 'Delayed']), list: true, default: 'not_started' }),
      c('dependsOn', 'depends_on', 'After (phase name)', 'text'),
      c('ownerId', 'owner_id', 'Owner', 'user', { list: true }),
      c('baselineStart', 'baseline_start', 'Baseline start', 'date'),
      c('baselineEnd', 'baseline_end', 'Baseline finish', 'date'),
    ],
  },
  {
    key: 'schedule', table: 'project_schedule', label: 'Engineer schedule', singular: 'Site assignment', order: 'start_date desc, created_at desc',
    cols: [
      c('userId', 'user_id', 'Engineer / technician', 'user', { required: true, list: true }),
      c('startDate', 'start_date', 'From', 'date', { required: true, list: true }),
      c('endDate', 'end_date', 'To', 'date', { required: true, list: true }),
      c('location', 'location', 'Site / location', 'text', { list: true }),
      c('task', 'task', 'Work', 'text', { list: true }),
    ],
  },
  {
    key: 'lookahead', table: 'project_lookahead', label: 'Two-week look-ahead', singular: 'Activity', order: 'start_date nulls last, created_at',
    cols: [
      c('activity', 'activity', 'Activity', 'text', { required: true, list: true }),
      c('system', 'system', 'System', 'select', { options: SYSTEMS, list: true }),
      c('area', 'area', 'Floor / zone', 'text', { list: true }),
      c('startDate', 'start_date', 'Start', 'date', { list: true }),
      c('endDate', 'end_date', 'Finish', 'date', { list: true }),
      c('ownerId', 'owner_id', 'Owner', 'user', { list: true }),
      c('blocker', 'blocker', 'Blocked by', 'select', { options: o(['none', 'Nothing'], ['material', 'Material'], ['approval', 'Approval / submittal'], ['rfi', 'RFI answer'], ['access', 'Site access'], ['manpower', 'Manpower'], ['other', 'Other']), list: true, default: 'none' }),
      c('blockerNote', 'blocker_note', 'Blocker details', 'text'),
      c('status', 'status', 'Status', 'select', { options: o(['planned', 'Planned'], ['in_progress', 'In progress'], ['done', 'Done'], ['blocked', 'Blocked']), list: true, default: 'planned' }),
    ],
  },
  // --- Site ---------------------------------------------------------------------------------------
  {
    key: 'attendance', table: 'project_attendance', label: 'Manpower & attendance', singular: 'Attendance entry', order: 'work_date desc, created_at desc',
    cols: [
      c('workDate', 'work_date', 'Date', 'date', { required: true, list: true }),
      c('person', 'person', 'Name / crew', 'text', { required: true, list: true }),
      c('company', 'company', 'Company', 'text', { list: true, default: 'Danat' }),
      c('trade', 'trade', 'Trade', 'select', { options: o(['engineer', 'Engineer'], ['technician', 'Technician'], ['helper', 'Helper'], ['programmer', 'Programmer'], ['subcontractor', 'Subcontractor crew']), list: true, default: 'technician' }),
      c('headcount', 'headcount', 'People', 'number', { list: true, default: 1 }),
      c('hours', 'hours', 'Hours', 'number', { list: true, default: 8 }),
    ],
  },
  {
    key: 'inspections', table: 'project_inspections', label: 'Inspection requests', singular: 'Inspection request', refPrefix: 'IR', order: 'created_at desc',
    cols: [
      c('title', 'title', 'Work / material to inspect', 'text', { required: true, list: true }),
      c('type', 'type', 'Type', 'select', { options: o(['material', 'Material (MIR)'], ['work', 'Work (WIR)'], ['final', 'Final inspection']), list: true, default: 'work' }),
      c('system', 'system', 'System', 'select', { options: SYSTEMS, list: true }),
      c('location', 'location', 'Location', 'text', { list: true }),
      c('requestedDate', 'requested_date', 'Requested on', 'date', { list: true }),
      c('inspectionDate', 'inspection_date', 'Inspection date', 'date', { list: true }),
      c('status', 'status', 'Result', 'select', { options: o(['requested', 'Requested'], ['approved', 'Approved'], ['approved_comments', 'Approved with comments'], ['rejected', 'Rejected'], ['cancelled', 'Cancelled']), list: true, default: 'requested' }),
      c('ownerId', 'owner_id', 'Our engineer', 'user'),
      c('comments', 'comments', 'Consultant comments', 'textarea'),
    ],
  },
  // --- Commercial & money -------------------------------------------------------------------------
  {
    key: 'variations', table: 'project_variations', label: 'Variation orders', singular: 'Variation order', refPrefix: 'VO', finance: true, order: 'created_at desc',
    cols: [
      c('title', 'title', 'Variation', 'text', { required: true, list: true }),
      c('amount', 'amount', 'Amount (SAR, – for omissions)', 'money', { required: true, list: true }),
      c('daysImpact', 'days_impact', 'Time impact (days)', 'number', { list: true }),
      c('status', 'status', 'Status', 'select', { options: VO_STATUSES, list: true, default: 'draft' }),
      c('customerRef', 'customer_ref', 'Customer VO / letter no.', 'text', { list: true }),
      c('customerApprovedDate', 'customer_approved_date', 'Customer approved on', 'date'),
      c('decidedBy', 'decided_by', 'Approved internally by', 'user', { readOnly: true, list: true }),
      c('decidedAt', 'decided_at', 'Internal decision on', 'date', { readOnly: true }),
      c('decisionComment', 'decision_comment', 'Administrator comment', 'text', { readOnly: true }),
      c('description', 'description', 'Scope of the change', 'textarea'),
    ],
  },
  {
    key: 'invoices', table: 'project_invoices', label: 'Progress invoices', singular: 'Progress invoice', refPrefix: 'INV', finance: true, order: 'created_at desc',
    cols: [
      c('period', 'period', 'Period', 'text', { list: true }),
      c('invoiceDate', 'invoice_date', 'Invoice date', 'date', { list: true }),
      c('percentComplete', 'percent_complete', 'Work complete', 'percent', { list: true }),
      c('grossAmount', 'gross_amount', 'This invoice (SAR, before retention & VAT)', 'money', { required: true, list: true }),
      c('retentionPct', 'retention_pct', 'Retention %', 'percent'),
      c('vatPct', 'vat_pct', 'VAT %', 'percent', { default: 15 }),
      c('retentionAmount', 'retention_amount', 'Retention', 'money', { readOnly: true, list: true }),
      c('vatAmount', 'vat_amount', 'VAT', 'money', { readOnly: true }),
      c('netAmount', 'net_amount', 'Net payable', 'money', { readOnly: true, list: true }),
      c('status', 'status', 'Status', 'select', { options: o(['draft', 'Draft'], ['submitted', 'Submitted'], ['approved', 'Approved by customer'], ['paid', 'Paid']), list: true, default: 'draft' }),
      c('customerRef', 'customer_ref', 'Customer approval ref.', 'text'),
      c('paidDate', 'paid_date', 'Paid on', 'date'),
    ],
  },
  {
    key: 'budget', table: 'project_budget', label: 'Budget', singular: 'Budget line', finance: true, order: 'system, kind',
    cols: [
      c('system', 'system', 'System', 'select', { options: SYSTEMS, required: true, list: true }),
      c('kind', 'kind', 'Cost type', 'select', { options: COST_KINDS, list: true, default: 'material' }),
      c('amount', 'amount', 'Budget (SAR)', 'money', { required: true, list: true }),
      c('notes', 'notes', 'Notes', 'text', { list: true }),
    ],
  },
  // --- Quality & safety ---------------------------------------------------------------------------
  {
    key: 'ncrs', table: 'project_ncrs', label: 'Non-conformance reports', singular: 'NCR', refPrefix: 'NCR', order: 'created_at desc',
    cols: [
      c('title', 'title', 'Non-conformance', 'text', { required: true, list: true }),
      c('system', 'system', 'System', 'select', { options: SYSTEMS, list: true }),
      c('location', 'location', 'Location', 'text'),
      c('raisedBy', 'raised_by', 'Raised by', 'select', { options: o(['internal', 'Internal'], ['consultant', 'Consultant'], ['client', 'Client'], ['contractor', 'Main contractor']), list: true, default: 'consultant' }),
      c('raisedDate', 'raised_date', 'Raised on', 'date', { list: true }),
      c('ownerId', 'owner_id', 'Owner', 'user', { list: true }),
      c('dueDate', 'due_date', 'Close by', 'date', { list: true }),
      c('status', 'status', 'Status', 'select', { options: o(['open', 'Open'], ['action_taken', 'Action taken'], ['closed', 'Closed']), list: true, default: 'open' }),
      c('rootCause', 'root_cause', 'Root cause', 'textarea'),
      c('correctiveAction', 'corrective_action', 'Corrective action', 'textarea'),
    ],
  },
  {
    key: 'hse', table: 'project_hse', label: 'Health, safety & environment', singular: 'HSE record', refPrefix: 'HSE', order: 'event_date desc nulls last, created_at desc',
    cols: [
      c('kind', 'kind', 'Kind', 'select', { options: o(['incident', 'Incident'], ['near_miss', 'Near miss'], ['toolbox_talk', 'Toolbox talk'], ['permit', 'Permit to work']), list: true, default: 'toolbox_talk' }),
      c('title', 'title', 'Subject', 'text', { required: true, list: true }),
      c('eventDate', 'event_date', 'Date', 'date', { list: true }),
      c('location', 'location', 'Location', 'text', { list: true }),
      c('severity', 'severity', 'Severity', 'select', { options: o(['low', 'Low'], ['medium', 'Medium'], ['high', 'High']), list: true }),
      c('validUntil', 'valid_until', 'Permit valid until', 'date'),
      c('people', 'people', 'People (attendees / involved)', 'text'),
      c('description', 'description', 'What happened / topics', 'textarea'),
      c('action', 'action', 'Action taken', 'textarea'),
      c('status', 'status', 'Status', 'select', { options: o(['open', 'Open'], ['closed', 'Closed']), list: true, default: 'closed' }),
    ],
  },];

export const sectionOf = (key: string) => PROJECT_SECTIONS.find((s) => s.key === key);

export const poDecisionSchema = z.object({ approve: z.boolean(), comment: z.string().trim().max(1000).optional() }).strict()
  .refine((v) => v.approve || !!v.comment, { path: ['comment'], message: 'Give the reason for rejecting' });

/** Standard testing & commissioning checks per system, added when a project starts. */
export const DEFAULT_TESTS: Record<string, string[]> = {
  cctv: ['Cameras powered and online', 'Recording on NVR verified', 'Image quality and angles approved', 'Retention period verified', 'Remote viewing tested'],
  structured_cabling: ['Cabling certified (Fluke test)', 'Racks dressed and labelled', 'Test reports handed over'],
  network_wifi: ['Switches configured and labelled', 'Wi-Fi coverage survey', 'Internet and VLANs tested'],
  access_control: ['Doors open/close by card', 'Fire alarm release tested', 'Exit buttons and break-glass tested', 'Users and schedules configured'],
  fire_alarm: ['Detectors tested', 'Manual call points tested', 'Sounders and strobes tested', 'Interfaces (lifts, HVAC, doors) tested', 'Panel and loop reports printed'],
  pa_va: ['Speakers tested per zone', 'Paging and announcements tested', 'Emergency evacuation message tested'],
  grms: ['Room controllers online', 'Lighting, curtains and HVAC scenes tested', 'Integration with the hotel system tested'],
  bms: ['Points list verified', 'Graphics and alarms tested', 'Integrations tested'],
  av: ['Displays and projectors tested', 'Audio and microphones tested', 'Control system tested'],
};

// --- the project ----------------------------------------------------------------------------------

export interface DeliveryProject {
  id: string;
  number: string;
  name: string;
  status: string;
  /** Null for projects added without a deal (older projects). */
  opportunityId: string | null;
  opportunityName: string | null;
  accountId: string | null;
  accountName: string | null;
  managerId: string | null;
  managerName: string | null;
  engineerIds: string[];
  /** Sales and others the project manager added to follow the project (read-only, with its updates). */
  followerIds: string[];
  contractValue: number | null;
  poNumber: string | null;
  poDate: string | null;
  startDate: string | null;
  plannedEnd: string | null;
  forecastEnd: string | null;
  acceptanceDate: string | null;
  warrantyMonths: number;
  warrantyEnd: string | null;
  retentionPct: number | null;
  retentionReleaseDate: string | null;
  labourRate: number | null;
  quotedMargin: number | null;
  notes: string | null;
  plannedManpower: number | null;
  customerReportEmail: string | null;
  sendCustomerReport: boolean;
  checklist: Record<string, { done: boolean; by?: string | null; at?: string | null }>;
  projectType: ProjectType;
  createdAt: string;
  intel: ProjectIntel;
  /** What the viewer may do. */
  canManage: boolean;
  /** False for the deal owner (sales), who follows the project read-only. */
  canWork: boolean;
  /** Logistics-only viewer: operational information only (commercial fields are removed by the server). */
  logisticsView: boolean;
}

export const projectUpdateSchema = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  status: z.enum(PROJECT_STATUSES.map((s) => s.value) as [string, ...string[]]).optional(),
  projectType: projectTypeKey.optional(),
  managerId: z.string().uuid().nullable().optional(),
  engineerIds: z.array(z.string().uuid()).max(30).optional(),
  followerIds: z.array(z.string().uuid()).max(50).optional(),
  contractValue: z.number().min(0).max(1e13).nullable().optional(),
  poNumber: z.string().trim().max(100).nullable().optional(),
  poDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  plannedEnd: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  forecastEnd: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  acceptanceDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  warrantyMonths: z.number().int().min(0).max(120).optional(),
  retentionPct: z.number().min(0).max(100).nullable().optional(),
  retentionReleaseDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  labourRate: z.number().min(0).max(1e6).nullable().optional(),
  notes: z.string().trim().max(4000).nullable().optional(),
  plannedManpower: z.number().int().min(0).max(10000).nullable().optional(),
  customerReportEmail: z.preprocess((v) => (v === '' ? null : v), z.string().trim().email('Enter a valid email').max(255).nullable()).optional(),
  sendCustomerReport: z.boolean().optional(),
  /** Tick or untick one checklist item. */
  check: z.object({ key: z.string().max(40), done: z.boolean() }).optional(),
}).strict();

// --- Project Intelligence -------------------------------------------------------------------------

export const PROJECT_WARNINGS = o(
  ['late', 'Past the planned finish date'], ['slipping', 'Forecast finish is after the planned finish'], ['behind', 'Progress behind schedule'],
  ['submittals_overdue', 'Submittal replies overdue'], ['resubmit', 'Submittals to revise & resubmit'], ['rfis_overdue', 'RFIs overdue'],
  ['materials_late', 'Material deliveries late'], ['materials_unordered', 'Materials not ordered yet'], ['high_risks', 'Open high risks / issues'],
  ['tests_failed', 'Failed tests'], ['snags_open', 'Open snags'], ['tickets_overdue', 'Service tickets overdue'],
  ['over_budget', 'Cost above the contract margin'], ['margin_low', 'Margin below quoted'], ['invoices_overdue', 'Invoices overdue'],
  ['handover_incomplete', 'Start checklist incomplete'], ['warranty_ending', 'Warranty ends within 60 days'],
  ['phases_late', 'Timeline phases past their finish'], ['inspections_rejected', 'Inspections rejected'], ['ncr_open', 'Open non-conformance reports'],
  ['hse_open', 'Open safety incidents'], ['lookahead_blocked', 'Look-ahead activities blocked'],
  ['no_plan', 'No planned finish date (14+ days)'], ['stuck_handover', 'Still in handover from sales (14+ days)'],
);

export interface ProjectIntel {
  score: number;
  health: 'green' | 'yellow' | 'red';
  percent: number;
  /** Expected % complete by today, from the start and planned finish dates. */
  expectedPercent: number | null;
  warnings: string[];
  counts: {
    submittalsOverdue: number; resubmit: number; rfisOverdue: number; materialsLate: number; materialsUnordered: number;
    highRisks: number; testsFailed: number; testsPending: number; testsTotal: number; snagsOpen: number; ticketsOpen: number; ticketsOverdue: number;
    invoicesOverdue: number; phasesLate: number; inspectionsRejected: number; ncrOpen: number; hseOpen: number; lookaheadBlocked: number;
  };
  /** Points per system rolled up for the whole project (null when none are entered). */
  points: PointsSummary | null;
  /** Managers only (null for engineers). */
  money: { contractValue: number; variations: number; cost: number; labourHours: number; margin: number | null; quotedMargin: number | null; invoiced: number; paid: number } | null;
}

export interface ProjectIntelInput {
  today: string;
  status: string;
  startDate: string | null;
  plannedEnd: string | null;
  forecastEnd: string | null;
  warrantyEnd: string | null;
  areaPercents: number[];
  /** Systems & points totals; when present they set the % complete. */
  points?: PointsTotals | null;
  startChecklistDone: number;
  /** Configuration-only projects: progress follows the configuration and handover checklists. */
  projectType?: ProjectType;
  configChecklistDone?: number;
  handoverChecklistDone?: number;
  /** Supply-only projects: progress follows the supply checklist. */
  supplyChecklistDone?: number;
  /** The project's type, when it has its own checklist (Setup → Project types): progress follows it. */
  typeChecklist?: { done: number; total: number; handoverDone: number; handoverTotal: number } | null;
  counts: ProjectIntel['counts'];
  contractValue: number | null;
  /** Variation orders approved by the customer (added to the contract). */
  variations: number;
  cost: number;
  labourHours: number;
  labourRate: number | null;
  quotedMargin: number | null;
  invoiced: number;
  paid: number;
}

/** The stages each point goes through, and how much of the work each one is. */
export const POINT_STAGES = [
  { key: 'cabled', label: 'Cabled', weight: 30 },
  { key: 'installed', label: 'Installed', weight: 30 },
  { key: 'configured', label: 'Terminated & configured', weight: 20 },
  { key: 'tested', label: 'Tested & done', weight: 20 },
] as const;
export type PointStage = (typeof POINT_STAGES)[number]['key'];
export type PointsTotals = { total: number } & Record<PointStage, number>;
export interface PointsSummary extends PointsTotals {
  /** Work done, weighted by stage (0-100). */
  percent: number;
}

/** Adds up points lines (all of a project, or one system's) and weighs the stages into a % complete. */
export function summarizePoints(rows: readonly object[]): PointsSummary | null {
  const t: PointsTotals = { total: 0, cabled: 0, installed: 0, configured: 0, tested: 0 };
  for (const line of rows) {
    const r = line as Partial<Record<'total' | PointStage, unknown>>;
    t.total += Number(r.total ?? 0);
    for (const s of POINT_STAGES) t[s.key] += Math.min(Number(r[s.key] ?? 0), Number(r.total ?? 0));
  }
  if (t.total <= 0) return null;
  const percent = Math.round(POINT_STAGES.reduce((a, s) => a + (t[s.key] / t.total) * s.weight, 0));
  return { ...t, percent };
}

const STATUS_PERCENT: Record<string, number> = { handover: 0, engineering: 10, procurement: 25, installation: 40, testing: 85, customer_handover: 95, warranty: 100, closed: 100, on_hold: 0 };
const days = (a: string, b: string) => Math.round((Date.parse(b) - Date.parse(a)) / 86400_000);

/** Health score (0–100), colour and warnings for a project. Pure: the same on the server and in tests. */
export function computeProjectIntel(i: ProjectIntelInput, withMoney: boolean): ProjectIntel {
  const done = ['warranty', 'closed'].includes(i.status);
  const points = i.points && i.points.total > 0 ? summarizePoints([i.points]) : null;
  const steps = CONFIG_CHECKLIST.length + HANDOVER_CHECKLIST.length;
  const tc = i.typeChecklist;
  // The organization's type is known (tc set): its checklist decides; with none, progress follows points / areas / status.
  const percent = done ? 100
    : tc && tc.total > 0 ? Math.round(((tc.done + tc.handoverDone) / (tc.total + tc.handoverTotal)) * 100)
    : tc ? (points ? points.percent : i.areaPercents.length ? Math.round(i.areaPercents.reduce((a, b) => a + b, 0) / i.areaPercents.length) : (STATUS_PERCENT[i.status] ?? 0))
    : i.projectType === 'configuration' ? Math.round((((i.configChecklistDone ?? 0) + (i.handoverChecklistDone ?? 0)) / steps) * 100)
    : i.projectType === 'supply' ? Math.round(((i.supplyChecklistDone ?? 0) / SUPPLY_CHECKLIST.length) * 100)
    : points ? points.percent : i.areaPercents.length ? Math.round(i.areaPercents.reduce((a, b) => a + b, 0) / i.areaPercents.length) : (STATUS_PERCENT[i.status] ?? 0);
  let expectedPercent: number | null = null;
  if (i.startDate && i.plannedEnd && i.plannedEnd > i.startDate) {
    expectedPercent = Math.max(0, Math.min(100, Math.round((days(i.startDate, i.today) / days(i.startDate, i.plannedEnd)) * 100)));
  }
  const w: [string, number][] = [];
  const k = i.counts;
  if (!done && i.plannedEnd && i.plannedEnd < i.today) w.push(['late', 25]);
  else if (!done && i.plannedEnd && i.forecastEnd && i.forecastEnd > i.plannedEnd) w.push(['slipping', 12]);
  if (!done && expectedPercent != null && percent + 15 < expectedPercent) w.push(['behind', 15]);
  if (k.submittalsOverdue) w.push(['submittals_overdue', Math.min(15, 5 * k.submittalsOverdue)]);
  if (k.resubmit) w.push(['resubmit', Math.min(10, 4 * k.resubmit)]);
  if (k.rfisOverdue) w.push(['rfis_overdue', Math.min(10, 4 * k.rfisOverdue)]);
  if (k.materialsLate) w.push(['materials_late', Math.min(15, 5 * k.materialsLate)]);
  if (k.materialsUnordered && ['installation', 'testing'].includes(i.status)) w.push(['materials_unordered', 8]);
  if (k.highRisks) w.push(['high_risks', Math.min(15, 6 * k.highRisks)]);
  if (k.testsFailed) w.push(['tests_failed', Math.min(10, 3 * k.testsFailed)]);
  if (k.snagsOpen && ['testing', 'customer_handover'].includes(i.status)) w.push(['snags_open', Math.min(10, 2 * k.snagsOpen)]);
  if (k.ticketsOverdue) w.push(['tickets_overdue', Math.min(10, 5 * k.ticketsOverdue)]);
  if (k.invoicesOverdue) w.push(['invoices_overdue', Math.min(10, 5 * k.invoicesOverdue)]);
  if (i.status !== 'handover' && i.startChecklistDone < START_CHECKLIST.length) w.push(['handover_incomplete', 5]);
  if (i.warrantyEnd && i.warrantyEnd >= i.today && days(i.today, i.warrantyEnd) <= 60) w.push(['warranty_ending', 0]);
  if (k.phasesLate) w.push(['phases_late', Math.min(15, 5 * k.phasesLate)]);
  if (k.inspectionsRejected) w.push(['inspections_rejected', Math.min(10, 4 * k.inspectionsRejected)]);
  if (k.ncrOpen) w.push(['ncr_open', Math.min(10, 3 * k.ncrOpen)]);
  if (k.hseOpen) w.push(['hse_open', Math.min(15, 8 * k.hseOpen)]);
  if (k.lookaheadBlocked) w.push(['lookahead_blocked', Math.min(10, 3 * k.lookaheadBlocked)]);
  // A project running two weeks with no finish date, or still at the handover from sales: no plan yet, not "all green".
  const running = i.startDate ? days(i.startDate, i.today) : 0;
  if (!done && !i.plannedEnd && running > 14) w.push(['no_plan', 15]);
  if (i.status === 'handover' && running > 14) w.push(['stuck_handover', 10]);

  const labour = i.labourHours * (i.labourRate ?? 0);
  const cost = i.cost + labour;
  const contract = (i.contractValue ?? 0) + i.variations;
  // No cost recorded yet (no supplier PO, cost or hours): no margin to show, rather than a false 100%.
  const margin = contract > 0 && cost > 0 ? Math.round(((contract - cost) / contract) * 1000) / 10 : null;
  if (contract > 0 && cost > contract) w.push(['over_budget', 15]);
  else if (margin != null && i.quotedMargin != null && cost > 0 && margin < i.quotedMargin - 5 && percent >= 50) w.push(['margin_low', 8]);

  const score = Math.max(0, 100 - w.reduce((a, [, p]) => a + p, 0));
  const health = score >= 75 ? 'green' : score >= 50 ? 'yellow' : 'red';
  const visible = withMoney ? w : w.filter(([key]) => !['over_budget', 'margin_low', 'invoices_overdue'].includes(key));
  return {
    score, health, percent, expectedPercent, points, warnings: visible.map(([key]) => key), counts: withMoney ? k : { ...k, invoicesOverdue: 0 },
    money: withMoney ? { contractValue: contract, variations: i.variations, cost, labourHours: i.labourHours, margin, quotedMargin: i.quotedMargin, invoiced: i.invoiced, paid: i.paid } : null,
  };
}

// --- High priority on a project or one of its items -----------------------------------------------

export interface ProjectPriority {
  id: string;
  projectId: string;
  projectNumber: string;
  projectName: string;
  section: string | null;
  rowId: string | null;
  itemLabel: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected' | 'cleared';
  requestedByName: string;
  requestedAt: string;
  decidedByName: string | null;
  comment: string | null;
}

export const priorityRequestSchema = z.object({
  /** An item of the project (both), or the whole project (neither). */
  section: z.string().max(40).optional(),
  rowId: z.string().uuid().optional(),
  reason: z.string().trim().min(3, 'Say why it needs High priority').max(1000),
}).strict().refine((v) => !!v.section === !!v.rowId, { path: ['rowId'], message: 'Choose the item' });

// --- Home for project managers and engineers ------------------------------------------------------

/** One thing to follow up, with where it lives. */
export interface FollowUp {
  projectId: string;
  projectNumber: string;
  section: string;
  rowId: string;
  ref: string | null;
  title: string;
  /** What's wrong or coming, e.g. "Reply overdue", "Delivery late". */
  reason: string;
  date: string | null;
  late: boolean;
}

export interface ProjectsHome {
  projects: { id: string; number: string; name: string; accountName: string | null; status: string; health: string; score: number; percent: number; expectedPercent: number | null; plannedEnd: string | null; warnings: string[]; highPriority: boolean }[];
  priorities: ProjectPriority[];
  followUps: FollowUp[];
  counts: { overdue: number; dueSoon: number; tickets: number; ordersWaiting: number };
}
// --- Project templates ----------------------------------------------------------------------------

/** What a template adds to a project: timeline (relative to the start), testing checks and work areas. */
export interface TemplatePayload {
  phases: { name: string; kind: 'phase' | 'milestone'; offsetDays: number; durationDays: number }[];
  tests: { system: string; item: string }[];
  areas: { system: string; area: string }[];
}
export interface ProjectTemplate { key: string; name: string; description: string; builtIn: boolean }

const ph = (name: string, offsetDays: number, durationDays: number) => ({ name, kind: 'phase' as const, offsetDays, durationDays });
const ms = (name: string, offsetDays: number) => ({ name, kind: 'milestone' as const, offsetDays, durationDays: 0 });

export const BUILTIN_TEMPLATES: (ProjectTemplate & { payload: TemplatePayload })[] = [
  {
    key: 'builtin:elv_full', builtIn: true, name: 'Full ELV project (hotel / building)', description: 'About 5 months: submittals, procurement, installation by floor, testing and handover.',
    payload: {
      phases: [
        ph('Mobilisation & handover from sales', 0, 7), ph('Engineering & submittals', 7, 30), ms('Material submittals approved', 37),
        ph('Procurement', 25, 45), ms('Main material on site', 70), ph('Installation', 45, 90), ph('Testing & commissioning', 125, 20),
        ms('Practical completion', 145), ph('Handover to customer', 145, 10), ms('Customer acceptance', 155),
      ],
      tests: [], areas: [],
    },
  },
  {
    key: 'builtin:single_system', builtIn: true, name: 'Single system (e.g. CCTV only)', description: 'About 2 months: submittal, delivery, installation, testing and handover.',
    payload: {
      phases: [
        ph('Mobilisation', 0, 3), ph('Submittal & approval', 3, 14), ph('Procurement & delivery', 10, 25), ph('Installation', 30, 20),
        ph('Testing & commissioning', 50, 7), ms('Customer acceptance', 60),
      ],
      tests: [], areas: [],
    },
  },
  {
    key: 'builtin:small_fitout', builtIn: true, name: 'Small fit-out (2–4 weeks)', description: 'Quick job: site survey, installation, testing and handover.',
    payload: { phases: [ph('Site survey', 0, 2), ph('Installation', 2, 14), ph('Testing', 16, 3), ms('Handover', 20)], tests: [], areas: [] },
  },
];

export const templateApplySchema = z.object({ key: z.string().min(1).max(100) }).strict();
export const templateSaveSchema = z.object({ name: z.string().trim().min(1).max(120), description: z.string().trim().max(500).optional() }).strict();
export interface PaymentTerm {
  name: string; percent: number; creditDays: number | null;
  /** Billed with a PI first, or straight with a tax invoice (null: not said; an advance is then a PI). */
  docType: 'pi' | 'tax' | null;
}

/** Saved on a won deal whose PO gives no payment terms: then the project type's default terms are used. */
export const PAYMENT_TERMS_NOT_STATED = 'Not stated in the PO';

/**
 * A customer PO's payment terms, one payment per line: "name | % of the contract | credit days | PI or Tax" (the last
 * two optional, either order), e.g. "Advance payment | 30 | PI" or "Material delivery | 60 | 30 days". Errors say which
 * line is wrong; the payments should add up to 100%. PAYMENT_TERMS_NOT_STATED reads as no terms and no error.
 */
export function parsePaymentTerms(text: string | null | undefined): { terms: PaymentTerm[]; total: number; errors: string[]; notStated: boolean } {
  const terms: PaymentTerm[] = [];
  const errors: string[] = [];
  if ((text ?? '').trim().toLowerCase() === PAYMENT_TERMS_NOT_STATED.toLowerCase()) return { terms, total: 0, errors, notStated: true };
  const lines = (text ?? '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  lines.forEach((line, i) => {
    const [name = '', pct = '', ...rest] = line.split('|').map((x) => x.trim());
    const percent = Number(pct.replace('%', '').trim());
    const doc = rest.find((x) => /^(pi|proforma.*|tax.*)$/i.test(x));
    const days = rest.find((x) => x !== doc && x !== '');
    const d = days ? Number(/(\d+)/.exec(days)?.[1] ?? NaN) : null;
    if (!name || !pct || !Number.isFinite(percent) || percent <= 0 || percent > 100) errors.push(`Line ${i + 1}: write it as “name | %”, e.g. “Advance payment | 30”.`);
    else if (d !== null && (!Number.isInteger(d) || d < 0 || d > 365)) errors.push(`Line ${i + 1}: credit days should be a number of days (0 to 365).`);
    else terms.push({ name: name.slice(0, 120), percent, creditDays: d, docType: doc ? (/^tax/i.test(doc) ? 'tax' : 'pi') : null });
  });
  if (terms.length > 12) errors.push('At most 12 payments.');
  const total = Math.round(terms.reduce((a, t) => a + t.percent, 0) * 100) / 100;
  if (terms.length && !errors.length && Math.abs(total - 100) > 0.01) errors.push(`The payments add up to ${total}%, not 100%.`);
  return { terms, total, errors, notStated: false };
}

/** The text saved on the deal, one payment per line ("Advance payment | 10 | PI", "Material delivery | 70 | 30 days | Tax"). */
export function formatPaymentTerms(terms: PaymentTerm[]): string {
  return terms.map((t) => [t.name.trim(), String(t.percent), t.creditDays != null ? `${t.creditDays} days` : null, t.docType === 'pi' ? 'PI' : t.docType === 'tax' ? 'Tax' : null]
    .filter((x): x is string => x != null).join(' | ')).join('\n');
}

/** A payment billed with a PI first: as set on the terms, else an advance / down payment. */
export const termIsPi = (t: { name: string; docType: 'pi' | 'tax' | null }) => t.docType ? t.docType === 'pi' : /advance|down ?payment|mobili[sz]ation|دفعة مقدمة|مقدم/i.test(t.name);

/** The common terms, one click each (the advance is billed with a PI). */
export const PAYMENT_TERM_PRESETS: { key: string; label: string; terms: PaymentTerm[] }[] = [
  { key: '10-70-20', label: '10 / 70 / 20', terms: [
    { name: 'Advance payment', percent: 10, creditDays: null, docType: 'pi' }, { name: 'Material delivery', percent: 70, creditDays: 30, docType: 'tax' }, { name: 'Handover', percent: 20, creditDays: 30, docType: 'tax' }] },
  { key: '30-60-10', label: '30 / 60 / 10', terms: [
    { name: 'Advance payment', percent: 30, creditDays: null, docType: 'pi' }, { name: 'Material delivery', percent: 60, creditDays: 30, docType: 'tax' }, { name: 'Handover', percent: 10, creditDays: 30, docType: 'tax' }] },
  { key: '50-50', label: '50 / 50', terms: [
    { name: 'Advance payment', percent: 50, creditDays: null, docType: 'pi' }, { name: 'Material delivery', percent: 50, creditDays: 30, docType: 'tax' }] },
  { key: '100-delivery', label: '100% on delivery', terms: [{ name: 'Material delivery', percent: 100, creditDays: 30, docType: 'tax' }] },
];
